import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trainingApi } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import RegistersTab from '../components/training/RegistersTab';
import EvaluationsTab from '../components/training/EvaluationsTab';
import ManualTab from '../components/training/ManualTab';
import VideosTab from '../components/training/VideosTab';
import DocumentsTab from '../components/training/DocumentsTab';

const STD_TABS = [
  { key: 'registers', def: 'Registri formativi', render: (meta) => <RegistersTab meta={meta} /> },
  { key: 'evaluations', def: 'Schede di valutazione', render: (meta) => <EvaluationsTab meta={meta} /> },
  { key: 'manual', def: 'Manuale', render: (meta) => <ManualTab meta={meta} /> },
  { key: 'videos', def: 'Video tutorial', render: (meta) => <VideosTab meta={meta} /> },
  { key: 'documents', def: 'Documenti', render: (meta) => <DocumentsTab meta={meta} /> },
];

export default function TrainingPage() {
  const [meta, setMeta] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    trainingApi.meta()
      .then(r => setMeta(r.data))
      .catch(() => setMeta({ types: [], areas: [], modes: [], settings: {}, can_manage: false, can_evaluate: false, can_edit_template: false }));
  }, []);

  if (!meta) {
    return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
  }

  const s = meta.settings || {};
  const tabsCfg = s.tabs || {};
  const visible = STD_TABS.filter(t => tabsCfg[t.key]?.visible !== false);
  const custom = s.custom_tabs || [];
  const firstTab = visible[0]?.key || (custom[0] ? `custom-${custom[0].tab_id}` : 'registers');
  const requested = searchParams.get('tab');
  const validTabs = new Set([...visible.map(t => t.key), ...custom.map(t => `custom-${t.tab_id}`)]);
  const tab = requested && validTabs.has(requested) ? requested : firstTab;

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="training-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{s.page_title || 'Formazione'}</h1>
        {(s.page_subtitle ?? 'Registri formativi, schede di valutazione, manuale e documenti') && (
          <p className="text-slate-500 mt-1">{s.page_subtitle ?? 'Registri formativi, schede di valutazione, manuale e documenti'}</p>
        )}
      </div>

      <Tabs value={tab} onValueChange={v => setSearchParams({ tab: v })}>
        <TabsList className="flex-wrap h-auto">
          {visible.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`training-tab-${t.key}`}>
              {tabsCfg[t.key]?.label || t.def}
            </TabsTrigger>
          ))}
          {custom.map(t => (
            <TabsTrigger key={t.tab_id} value={`custom-${t.tab_id}`} data-testid={`training-tab-custom-${t.tab_id}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {visible.map(t => (
          <TabsContent key={t.key} value={t.key} className="mt-4">{t.render(meta)}</TabsContent>
        ))}
        {custom.map(t => (
          <TabsContent key={t.tab_id} value={`custom-${t.tab_id}`} className="mt-4">
            <div className="bg-white border border-slate-200 p-5 max-w-3xl whitespace-pre-wrap text-sm text-slate-700 leading-relaxed"
                 data-testid={`training-custom-content-${t.tab_id}`}>
              {t.content || 'Nessun contenuto per questo tab.'}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
